import { useEffect, useState, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, AreaChart, Area, ReferenceLine, Legend } from "recharts";
import { Users, TrendingUp, Euro, Eye, Search, Crown, Activity, Heart, Mail, Database, RefreshCw, ShieldCheck, AlertTriangle, CheckCircle2, Wrench } from "lucide-react";
import { toast } from "sonner";

function formatShortDate(iso) {
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
  } catch { return iso; }
}

function formatLongDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" });
  } catch { return iso; }
}

function formatCompact(n) {
  if (n === null || n === undefined) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function ChartTooltip({ active, payload, label, colors }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border border-white/15 bg-[#0a0a0f]/95 backdrop-blur-sm shadow-2xl p-3 min-w-[160px]">
      <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2 pb-2 border-b border-white/10">
        {formatLongDate(label)}
      </div>
      <div className="space-y-1.5">
        {payload.map((p, i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: p.color || colors?.[p.dataKey] }} />
              <span className="font-mono text-[10px] uppercase tracking-wider text-white/60">
                {p.name || p.dataKey}
              </span>
            </div>
            <span className="font-heading font-bold text-sm" style={{ color: p.color || colors?.[p.dataKey] }}>
              {(p.value ?? 0).toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChartMiniKpi({ label, value, accent }) {
  return (
    <div className="flex flex-col">
      <span className="font-mono text-[9px] text-slate-500 uppercase tracking-widest">{label}</span>
      <span className={`font-heading font-black text-lg tracking-tight ${accent || "text-slate-900"}`}>{value}</span>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className={`flat-card p-5 ${accent ? "border-[#FACC15]/40" : ""}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] text-white/40 uppercase tracking-widest">{label}</span>
        {Icon && <Icon className={`w-4 h-4 ${accent ? "text-[#FACC15]" : "text-white/40"}`} />}
      </div>
      <div className={`font-heading font-black text-3xl tracking-tighter ${accent ? "neon-yellow" : ""}`}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-white/40 tracking-wider mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminAnalytics() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [integrity, setIntegrity] = useState(null);
  const [integrityLoading, setIntegrityLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [repairStatus, setRepairStatus] = useState(null);
  const syncPollRef = useRef(null);
  const repairPollRef = useRef(null);

  useEffect(() => () => {
    if (syncPollRef.current) clearInterval(syncPollRef.current);
    if (repairPollRef.current) clearInterval(repairPollRef.current);
  }, []);

  const loadSyncStatus = async () => {
    try {
      const { data } = await api.get("/admin/sync/status");
      setSyncStatus(data);
    } catch {}
  };

  const loadIntegrity = async () => {
    setIntegrityLoading(true);
    try {
      const { data } = await api.get("/admin/sync/integrity");
      setIntegrity(data);
    } catch (e) {
      toast.error("Échec du healthcheck");
    } finally {
      setIntegrityLoading(false);
    }
  };

  const loadRepairStatus = async () => {
    try {
      const { data } = await api.get("/admin/sync/repair/status");
      setRepairStatus(data);
      return data;
    } catch { return null; }
  };

  const triggerRepair = async () => {
    setRepairing(true);
    try {
      const { data } = await api.post("/admin/sync/repair");
      if (data.status === "already_running") toast.info("Réparation déjà en cours…");
      else toast.success("Réparation lancée — récupère les cartes manquantes");
      if (repairPollRef.current) clearInterval(repairPollRef.current);
      let n = 0;
      repairPollRef.current = setInterval(async () => {
        n += 1;
        const s = await loadRepairStatus();
        if (s?.status === "completed" || n >= 60) {
          clearInterval(repairPollRef.current);
          repairPollRef.current = null;
          setRepairing(false);
          loadIntegrity();
          loadSyncStatus();
          if (s?.status === "completed") {
            toast.success(`Réparation terminée — ${s.added || 0} cartes ajoutées`);
          }
        }
      }, 5000);
    } catch (e) {
      toast.error("Échec du déclenchement");
      setRepairing(false);
    }
  };

  const triggerSync = async (full = false) => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync/trigger", null, { params: { full } });
      if (data.status === "already_running") toast.info("Sync déjà en cours…");
      else toast.success(full ? "Sync complet lancé (peut prendre 30-45 min)" : "Sync 24 mois lancé (~10 min)");
      if (syncPollRef.current) clearInterval(syncPollRef.current);
      let n = 0;
      syncPollRef.current = setInterval(async () => {
        n += 1;
        await loadSyncStatus();
        if (n >= 12) {
          clearInterval(syncPollRef.current);
          syncPollRef.current = null;
        }
      }, 5000);
    } catch (e) {
      toast.error("Échec du déclenchement");
    } finally {
      setSyncing(false);
    }
  };

  const [backfilling, setBackfilling] = useState(false);
  const [deduping, setDeduping] = useState(false);

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/admin/analytics");
      setData(r.data);
      if (silent) toast.success("Stats rafraîchies");
    } catch (e) {
      setError(e?.response?.status === 403 ? "Admin only" : "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const triggerBackfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/prices/backfill");
      if (data.status === "already_running") {
        toast.info("Actualisation des prix déjà en cours…");
      } else {
        toast.success("Actualisation des prix démarrée (~2-5 min en arrière-plan)");
      }
      // Poll status every 5s up to 5 min
      let n = 0;
      const poll = setInterval(async () => {
        n += 1;
        try {
          const s = await api.get("/admin/prices/backfill/status");
          if (!s.data.running || n >= 60) {
            clearInterval(poll);
            setBackfilling(false);
            if (s.data.scanned > 0) {
              toast.success(`Prix actualisés — ${s.data.inserted} nouvelles entrées (${s.data.scanned} cartes)`);
            }
            fetchData(true);
          }
        } catch { /* ignore */ }
      }, 5000);
    } catch (e) {
      toast.error("Échec de l'actualisation");
      setBackfilling(false);
    }
  };

  const triggerDedupe = async () => {
    setDeduping(true);
    try {
      const { data } = await api.post("/admin/prices/dedupe");
      toast.success(`Doublons supprimés — ${data.deleted} entrées effacées, ${data.remaining} restent`);
    } catch (e) {
      toast.error("Échec du dédoublonnage");
    } finally {
      setDeduping(false);
    }
  };

  const sendWeeklyDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/weekly-digest/send");
      toast.success(`Weekly digest dispatched · ${data.sent_attempts} sent, ${data.skipped} skipped`);
    } catch (e) {
      toast.error("Failed to send digest");
    } finally {
      setSendingDigest(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") {
      fetchData();
      loadSyncStatus();
      loadIntegrity();
      loadRepairStatus();
    } else if (user !== null) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (authLoading || user === null) return <div className="text-center text-white/40 font-mono py-32 tracking-widest text-xs">LOADING…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;
  if (loading) return <div className="text-center text-white/40 font-mono py-32 tracking-widest text-xs">LOADING…</div>;
  if (error || !data) return <div className="text-center text-[#FF007F] font-mono py-32">{error || "No data"}</div>;

  const series = data.traffic.series || [];

  return (
    <div className="page-enter max-w-[1440px] mx-auto px-6 lg:px-10 py-12">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-xs text-[#00E5FF] tracking-widest mb-2">// ADMIN_ANALYTICS · LIVE</div>
          <h1 className="font-heading font-black text-5xl tracking-tighter">Site Analytics</h1>
          <p className="text-white/50 mt-2 max-w-2xl text-sm">Real-time stats on traffic, users, revenue and engagement.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => fetchData()}
            disabled={loading}
            data-testid="refresh-analytics-btn"
            className="btn-ghost text-xs uppercase tracking-wider inline-flex items-center gap-1.5 border-[#00E5FF]/40 text-[#00E5FF]"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Refreshing..." : "Refresh stats"}
          </button>
          <button
            onClick={sendWeeklyDigest}
            disabled={sendingDigest}
            data-testid="send-weekly-digest-btn"
            className="btn-ghost text-xs uppercase tracking-wider inline-flex items-center gap-1.5 border-[#FACC15]/40 text-[#FACC15]"
          >
            <Mail className="w-3.5 h-3.5" />
            {sendingDigest ? "Sending..." : "Send weekly digest now"}
          </button>
        </div>
      </div>

      {/* Sync Status Card */}
      <div className="flat-card p-5 mb-8 border-[#00E5FF]/30" data-testid="sync-status-card">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-3">
          <div>
            <div className="font-mono text-[10px] text-[#00E5FF] uppercase tracking-widest mb-1 inline-flex items-center gap-1.5">
              <Database className="w-3 h-3" /> POKEMON_DATA_MIRROR
            </div>
            <h2 className="font-heading font-black text-2xl tracking-tighter">Local database sync</h2>
            <p className="text-white/50 text-xs mt-1 max-w-xl">
              Aspire automatiquement les sets et cartes de pokemontcg.io dans MongoDB. L'app sert ensuite ces données localement, sans dépendre de l'API externe.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => triggerSync(false)}
              disabled={syncing || syncStatus?.status?.status === "running"}
              data-testid="sync-trigger-24m-btn"
              className="btn-ghost text-xs uppercase tracking-wider inline-flex items-center gap-1.5 border-[#00E5FF]/40 text-[#00E5FF]"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${syncing || syncStatus?.status?.status === "running" ? "animate-spin" : ""}`} />
              Sync 24m
            </button>
            <button
              onClick={() => triggerSync(true)}
              disabled={syncing || syncStatus?.status?.status === "running"}
              data-testid="sync-trigger-full-btn"
              className="btn-ghost text-xs uppercase tracking-wider"
            >
              Sync FULL
            </button>
            <button
              onClick={triggerBackfill}
              disabled={backfilling}
              data-testid="prices-backfill-btn"
              className="btn-ghost text-xs uppercase tracking-wider inline-flex items-center gap-1.5 border-emerald-500/40 text-emerald-400"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${backfilling ? "animate-spin" : ""}`} />
              {backfilling ? "Actualisation..." : "Actualiser prix"}
            </button>
            <button
              onClick={triggerDedupe}
              disabled={deduping}
              data-testid="prices-dedupe-btn"
              className="btn-ghost text-xs uppercase tracking-wider inline-flex items-center gap-1.5 border-[#FACC15]/40 text-[#FACC15]"
            >
              {deduping ? "Nettoyage..." : "Dédoublonner"}
            </button>
          </div>
        </div>
        {syncStatus && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3 border-t border-white/10">
            <div>
              <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Status</div>
              <div className={`font-heading font-bold text-lg ${syncStatus.status?.status === "ok" ? "text-[#00E5FF]" : syncStatus.status?.status === "running" ? "text-[#FACC15]" : syncStatus.status?.status === "error" ? "text-[#FF007F]" : "text-white/60"}`}>
                {syncStatus.status?.status || "never_run"}
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Sets in DB</div>
              <div className="font-heading font-bold text-lg">{syncStatus.counts?.sets_in_db?.toLocaleString() || 0}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Cards in DB</div>
              <div className="font-heading font-bold text-lg">{syncStatus.counts?.cards_in_db?.toLocaleString() || 0}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Last finished</div>
              <div className="font-mono text-xs text-white/80">
                {syncStatus.status?.finished_at ? new Date(syncStatus.status.finished_at).toLocaleString() : "—"}
              </div>
              {syncStatus.status?.duration_seconds && (
                <div className="font-mono text-[10px] text-white/40 mt-0.5">{Math.round(syncStatus.status.duration_seconds)}s</div>
              )}
            </div>
          </div>
        )}
        {syncStatus?.status?.error && (
          <div className="mt-3 text-xs text-[#FF007F] font-mono">{syncStatus.status.error}</div>
        )}
      </div>

      {/* Integrity / Healthcheck Card */}
      <div className="flat-card p-5 mb-8 border-emerald-500/30" data-testid="integrity-card">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-3">
          <div>
            <div className="font-mono text-[10px] text-emerald-500 uppercase tracking-widest mb-1 inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3" /> DATA_INTEGRITY
            </div>
            <h2 className="font-heading font-black text-2xl tracking-tighter">Set completeness healthcheck</h2>
            <p className="text-white/50 text-xs mt-1 max-w-xl">
              Compare le nombre de cartes déclarées vs celles présentes en DB. Lance une réparation ciblée si certains sets sont incomplets.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadIntegrity}
              disabled={integrityLoading}
              data-testid="refresh-integrity-btn"
              className="btn-ghost text-xs uppercase tracking-wider inline-flex items-center gap-1.5 border-emerald-500/40 text-emerald-500"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${integrityLoading ? "animate-spin" : ""}`} />
              Rescan
            </button>
            <button
              onClick={triggerRepair}
              disabled={repairing || !integrity?.incomplete_count || repairStatus?.status === "running"}
              data-testid="repair-btn"
              className="btn-ghost text-xs uppercase tracking-wider inline-flex items-center gap-1.5 border-[#FACC15]/40 text-[#FACC15] disabled:opacity-30"
            >
              <Wrench className={`w-3.5 h-3.5 ${repairing ? "animate-spin" : ""}`} />
              {repairing || repairStatus?.status === "running" ? "Réparation…" : "Réparer"}
            </button>
          </div>
        </div>

        {integrity && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3 border-t border-white/10">
              <div>
                <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Sets OK</div>
                <div className="font-heading font-bold text-lg text-emerald-500 inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" />
                  {integrity.fully_complete}/{integrity.total_sets}
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Incomplete sets</div>
                <div className={`font-heading font-bold text-lg inline-flex items-center gap-1.5 ${integrity.incomplete_count > 0 ? "text-[#FACC15]" : "text-emerald-500"}`}>
                  {integrity.incomplete_count > 0 && <AlertTriangle className="w-4 h-4" />}
                  {integrity.incomplete_count}
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Cards in DB</div>
                <div className="font-heading font-bold text-lg">{integrity.total_cards_in_db.toLocaleString()}</div>
                <div className="font-mono text-[10px] text-white/40 mt-0.5">{integrity.total_cards_declared.toLocaleString()} declared</div>
              </div>
              <div>
                <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Coverage</div>
                <div className={`font-heading font-bold text-lg ${integrity.completion_pct === 100 ? "text-emerald-500" : "text-[#FACC15]"}`}>{integrity.completion_pct}%</div>
              </div>
            </div>

            {integrity.incomplete_sets?.length > 0 && (
              <div className="mt-4 pt-3 border-t border-white/10">
                <div className="font-mono text-[10px] text-[#FACC15] uppercase tracking-widest mb-2">
                  Incomplete sets ({integrity.incomplete_sets.length})
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-auto pr-2">
                  {integrity.incomplete_sets.map((s) => (
                    <div key={s.id} className="flex items-center gap-3 p-2 rounded bg-white/[0.02] border border-white/5" data-testid={`incomplete-set-${s.id}`}>
                      {s.logo && <img src={s.logo} alt={s.name} className="h-8 w-12 object-contain" />}
                      <div className="flex-1 min-w-0">
                        <div className="font-heading font-semibold text-sm truncate">{s.name}</div>
                        <div className="font-mono text-[10px] text-white/40">{s.id} · {s.releaseDate}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-sm text-[#FACC15]">{s.actual}/{s.declared}</div>
                        <div className="font-mono text-[10px] text-white/40">−{s.gap}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {repairStatus && repairStatus.status !== "never_run" && (
              <div className="mt-3 pt-3 border-t border-white/10 font-mono text-xs text-white/60">
                <span className="uppercase tracking-wider text-[10px] text-white/40 mr-2">Last repair:</span>
                {repairStatus.status}
                {repairStatus.processed !== undefined && ` · ${repairStatus.processed}/${repairStatus.targets || 0} sets`}
                {repairStatus.added !== undefined && ` · +${repairStatus.added} cards`}
                {repairStatus.finished_at && ` · ${new Date(repairStatus.finished_at).toLocaleString()}`}
              </div>
            )}
          </>
        )}
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total users" value={data.users.total} sub={`+${data.users.new_7d} last 7d`} icon={Users} />
        <StatCard label="Premium users" value={data.users.premium} sub={`${data.users.conversion_pct}% conversion`} icon={Crown} accent />
        <StatCard label="MRR" value={`€${data.revenue.mrr_eur.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub="monthly recurring" icon={Euro} accent />
        <StatCard label="Revenue 30d" value={`€${data.revenue.last_30d_eur.toLocaleString(undefined, { maximumFractionDigits: 2 })}`} sub={`€${data.revenue.total_paid_eur.toLocaleString(undefined, { maximumFractionDigits: 2 })} all-time`} icon={TrendingUp} />
      </div>

      {/* LIFETIME row — since site launch */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Pageviews lifetime"
          value={(data.traffic.lifetime_pageviews || 0).toLocaleString()}
          sub="depuis le lancement"
          icon={Eye}
        />
        <StatCard
          label="Unique visitors lifetime"
          value={(data.traffic.lifetime_unique_visitors || 0).toLocaleString()}
          sub="depuis le lancement"
          icon={Users}
        />
        <StatCard
          label="Croissance WoW"
          value={`${data.users.growth_wow_pct >= 0 ? "+" : ""}${data.users.growth_wow_pct ?? 0}%`}
          sub="inscriptions vs sem. -1"
          icon={TrendingUp}
          accent={data.users.growth_wow_pct > 0}
        />
        <StatCard
          label="Croissance MoM"
          value={`${data.users.growth_mom_pct >= 0 ? "+" : ""}${data.users.growth_mom_pct ?? 0}%`}
          sub="inscriptions vs mois -1"
          icon={TrendingUp}
          accent={data.users.growth_mom_pct > 0}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard label="Pageviews 30d" value={data.traffic.last_30d_pageviews.toLocaleString()} sub={`${data.traffic.last_30d_unique_visitors.toLocaleString()} unique`} icon={Eye} />
        <StatCard label="Card views" value={data.engagement.total_card_views.toLocaleString()} sub="all time" icon={Activity} />
        <StatCard label="Prices submitted" value={data.engagement.total_price_entries.toLocaleString()} sub={`+${data.engagement.price_entries_7d} last 7d`} icon={TrendingUp} />
        <StatCard label="Annonces actives" value={(data.engagement.active_sponsors ?? 0).toLocaleString()} sub={`${data.engagement.total_collection_items} cards in collections`} icon={Heart} />
      </div>

      {/* Traffic chart */}
      {(() => {
        const totalPv = series.reduce((s, x) => s + (x.pageviews || 0), 0);
        const totalUv = series.reduce((s, x) => s + (x.unique_visitors || 0), 0);
        const avgPv = series.length ? Math.round(totalPv / series.length) : 0;
        const peakPv = series.reduce((m, x) => Math.max(m, x.pageviews || 0), 0);
        const peakDay = series.find((x) => x.pageviews === peakPv);
        // Adaptive tick interval so labels never overlap
        const tickInterval = series.length > 20 ? Math.ceil(series.length / 10) - 1 : series.length > 12 ? 2 : 1;
        return (
          <div className="flat-card p-6 mb-10 relative overflow-hidden" data-testid="traffic-chart">
            {/* subtle radial glow */}
            <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#FACC15]/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-[#0EA5E9]/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative flex items-start justify-between flex-wrap gap-4 mb-5">
              <div>
                <div className="font-mono text-[10px] text-[#00E5FF] uppercase tracking-widest mb-1 inline-flex items-center gap-1.5">
                  <Activity className="w-3 h-3" /> TRAFFIC · LAST_30D
                </div>
                <h2 className="font-heading font-black text-2xl tracking-tighter">Trafic quotidien</h2>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#FACC15]/50 bg-[#FEF9C3] text-[#854D0E] font-mono text-[10px] uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#EAB308]" /> Pageviews
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#0EA5E9]/40 bg-[#E0F2FE] text-[#075985] font-mono text-[10px] uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0EA5E9]" /> Unique visitors
                </span>
              </div>
            </div>

            {/* Mini KPI summary */}
            {series.length > 0 && (
              <div className="relative grid grid-cols-2 md:grid-cols-4 gap-4 pb-5 mb-3 border-b border-slate-200">
                <ChartMiniKpi label="Total pageviews (30d)" value={totalPv.toLocaleString()} accent="text-[#B45309]" />
                <ChartMiniKpi label="Total unique (30d)" value={totalUv.toLocaleString()} accent="text-[#0369A1]" />
                <ChartMiniKpi label="Moyenne / jour" value={avgPv.toLocaleString()} />
                <ChartMiniKpi
                  label={peakDay ? `Pic · ${formatShortDate(peakDay.date)}` : "Pic quotidien"}
                  value={peakPv.toLocaleString()}
                />
              </div>
            )}

            {series.length > 0 ? (
              <div className="relative">
                <ResponsiveContainer width="100%" height={340}>
                  <AreaChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                    <defs>
                      <linearGradient id="gPv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#EAB308" stopOpacity={0.5} />
                        <stop offset="60%" stopColor="#EAB308" stopOpacity={0.12} />
                        <stop offset="100%" stopColor="#EAB308" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gUv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.35} />
                        <stop offset="60%" stopColor="#0EA5E9" stopOpacity={0.08} />
                        <stop offset="100%" stopColor="#0EA5E9" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(15,23,42,0.08)" vertical={false} strokeDasharray="3 6" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatShortDate}
                      tick={{ fill: "#64748B", fontSize: 11, fontFamily: "JetBrains Mono" }}
                      axisLine={{ stroke: "rgba(15,23,42,0.15)" }}
                      tickLine={false}
                      interval={tickInterval}
                      dy={6}
                    />
                    <YAxis
                      tickFormatter={formatCompact}
                      tick={{ fill: "#64748B", fontSize: 11, fontFamily: "JetBrains Mono" }}
                      axisLine={false}
                      tickLine={false}
                      width={46}
                    />
                    <Tooltip
                      content={<ChartTooltip colors={{ pageviews: "#FACC15", unique_visitors: "#00E5FF" }} />}
                      cursor={{ stroke: "rgba(15,23,42,0.25)", strokeWidth: 1, strokeDasharray: "3 4" }}
                    />
                    {avgPv > 0 && (
                      <ReferenceLine
                        y={avgPv}
                        stroke="rgba(180,83,9,0.4)"
                        strokeDasharray="4 4"
                        label={{ value: `Ø ${formatCompact(avgPv)}`, position: "right", fill: "#B45309", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      />
                    )}
                    <Area
                      type="monotone"
                      dataKey="pageviews"
                      name="Pageviews"
                      stroke="#EAB308"
                      fill="url(#gPv)"
                      strokeWidth={2.5}
                      activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2, fill: "#EAB308" }}
                    />
                    <Area
                      type="monotone"
                      dataKey="unique_visitors"
                      name="Unique visitors"
                      stroke="#0EA5E9"
                      fill="url(#gUv)"
                      strokeWidth={2.5}
                      activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2, fill: "#0EA5E9" }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center text-white/40 py-16 font-mono text-xs tracking-widest">NO TRAFFIC DATA YET</div>
            )}
          </div>
        );
      })()}

      {/* Signups chart (last 30d) */}
      {(() => {
        const signups = data.users.signups_series || [];
        const totalSignups = signups.reduce((s, x) => s + (x.signups || 0), 0);
        const avgSignups = signups.length ? (totalSignups / signups.length).toFixed(1) : 0;
        const peakSignups = signups.reduce((m, x) => Math.max(m, x.signups || 0), 0);
        const peakDay = signups.find((x) => x.signups === peakSignups);
        const tickInterval = signups.length > 20 ? Math.ceil(signups.length / 10) - 1 : signups.length > 12 ? 2 : 1;
        return (
          <div className="flat-card p-6 mb-10 relative overflow-hidden" data-testid="signups-chart">
            <div className="absolute -top-24 -right-24 w-72 h-72 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />

            <div className="relative flex items-start justify-between flex-wrap gap-4 mb-5">
              <div>
                <div className="font-mono text-[10px] text-emerald-400 uppercase tracking-widest mb-1 inline-flex items-center gap-1.5">
                  <Users className="w-3 h-3" /> SIGNUPS · LAST_30D
                </div>
                <h2 className="font-heading font-black text-2xl tracking-tighter">Nouvelles inscriptions</h2>
              </div>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-emerald-500/40 bg-emerald-50 text-emerald-800 font-mono text-[10px] uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> New users
              </span>
            </div>

            {signups.length > 0 && (
              <div className="relative grid grid-cols-3 gap-4 pb-5 mb-3 border-b border-slate-200">
                <ChartMiniKpi label="Total (30d)" value={totalSignups.toLocaleString()} accent="text-emerald-600" />
                <ChartMiniKpi label="Moyenne / jour" value={avgSignups} />
                <ChartMiniKpi
                  label={peakDay ? `Pic · ${formatShortDate(peakDay.date)}` : "Pic quotidien"}
                  value={peakSignups.toLocaleString()}
                />
              </div>
            )}

            {signups.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={signups} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                  <defs>
                    <linearGradient id="gSu" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.55} />
                      <stop offset="60%" stopColor="#10B981" stopOpacity={0.1} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(15,23,42,0.08)" vertical={false} strokeDasharray="3 6" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatShortDate}
                    tick={{ fill: "#64748B", fontSize: 11, fontFamily: "JetBrains Mono" }}
                    axisLine={{ stroke: "rgba(15,23,42,0.15)" }}
                    tickLine={false}
                    interval={tickInterval}
                    dy={6}
                  />
                  <YAxis
                    allowDecimals={false}
                    tickFormatter={formatCompact}
                    tick={{ fill: "#64748B", fontSize: 11, fontFamily: "JetBrains Mono" }}
                    axisLine={false}
                    tickLine={false}
                    width={40}
                  />
                  <Tooltip
                    content={<ChartTooltip colors={{ signups: "#10B981" }} />}
                    cursor={{ stroke: "rgba(15,23,42,0.25)", strokeWidth: 1, strokeDasharray: "3 4" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="signups"
                    name="New users"
                    stroke="#10B981"
                    fill="url(#gSu)"
                    strokeWidth={2.5}
                    activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2, fill: "#10B981" }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-white/40 py-12 font-mono text-xs tracking-widest">NO SIGNUPS IN LAST 30 DAYS</div>
            )}
          </div>
        );
      })()}

      {/* Top lists */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <div className="font-mono text-xs text-[#00E5FF] tracking-widest mb-3">// MOST_VIEWED_CARDS</div>
          {data.top_cards.length === 0 ? (
            <div className="flat-card p-8 text-center text-white/40 text-sm">No views yet</div>
          ) : (
            <div className="flat-card overflow-hidden">
              <ul className="divide-y divide-white/10">
                {data.top_cards.map((c, i) => (
                  <li key={c.card_id} className="px-4 py-2 flex items-center gap-3 text-sm">
                    <span className="font-mono text-white/30 w-6">{i + 1}</span>
                    <Link to={`/cards/${c.card_id}`} className="w-9 h-12 bg-black/40 border border-white/10 overflow-hidden flex-shrink-0">
                      {c.image && <img src={c.image} alt={c.name} className="w-full h-full object-cover" loading="lazy" />}
                    </Link>
                    <Link to={`/cards/${c.card_id}`} className="flex-1 min-w-0 hover:text-[#FACC15]">
                      <div className="font-heading font-bold text-sm truncate">{c.name || c.card_id}</div>
                      <div className="font-mono text-[10px] text-white/40 truncate">{c.set_name}</div>
                    </Link>
                    <span className="font-mono font-bold text-base">{c.views}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div>
          <div className="font-mono text-xs text-[#00E5FF] tracking-widest mb-3">// TOP_SEARCHES</div>
          {data.top_searches.length === 0 ? (
            <div className="flat-card p-8 text-center text-white/40 text-sm">No searches yet</div>
          ) : (
            <div className="flat-card p-4">
              <div className="flex flex-wrap gap-2">
                {data.top_searches.map((t) => (
                  <Link to={`/search?q=${encodeURIComponent(t.term)}`} key={t.term} className="lang-chip hover:border-[#FACC15]/60 hover:text-[#FACC15] transition inline-flex items-center gap-2">
                    <Search className="w-3 h-3" /> {t.term} <span className="text-white/40">{t.count}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <p className="text-center text-white/30 font-mono text-[10px] tracking-widest mt-10">
        // COMPUTED_AT · {new Date(data.computed_at).toLocaleString()}
      </p>
    </div>
  );
}
